import { assertNever } from "@/packages/ts-utils";
import { tagsToFields, toError } from "@listen-labs/o11y/logs";
import type {
  CaptureMessageLevel,
  CaptureRecord,
  LogMeta,
} from "@listen-labs/o11y/logs";
import * as Sentry from "@sentry/nextjs";

type SentryTagValue = string | number | boolean;

const isSentryTagValue = (value: unknown): value is SentryTagValue =>
  typeof value === "string" ||
  typeof value === "number" ||
  typeof value === "boolean";

const toSentryTags = (fields: LogMeta): Record<string, SentryTagValue> =>
  Object.fromEntries(
    Object.entries(fields).filter((entry): entry is [string, SentryTagValue] =>
      isSentryTagValue(entry[1])
    )
  );

const SENTRY_LEVELS: Record<CaptureMessageLevel, Sentry.SeverityLevel> = {
  warn: "warning",
  info: "info",
};

/** Logger `capture` sink: context and tags become Sentry tags, attributes become extra. */
export const captureInSentry = (record: CaptureRecord): void => {
  const { attributes, tags, context } = record;
  const scope = {
    tags: toSentryTags({ ...context, ...tagsToFields(tags) }),
    extra: attributes,
  };
  switch (record.kind) {
    case "exception":
      Sentry.captureException(toError(record.error), scope);
      return;
    case "message":
      Sentry.captureMessage(record.message, {
        ...scope,
        level: SENTRY_LEVELS[record.level],
      });
      return;
    default:
      assertNever(record);
  }
};
